import { createContext, useContext, useState, ReactNode } from 'react';

// Import the shared types
import { AnalysisResult } from '../types/analysis';

// Re-export the shared AnalysisResult type
export type { AnalysisResult };

interface AnalysisContextType {
  result: AnalysisResult | null;
  isLoading: boolean;
  /** Current pipeline stage while an analysis job is running */
  progressStage: string | null;
  error: string | null;
  analyzeText: (text: string, email?: string) => Promise<void>;
  analyzeFile: (file: File, email: string, country: string, privacyConsent: boolean, consentStorage?: boolean, consentImprovement?: boolean, industry?: string, stage?: string) => Promise<void>;
  clearResult: () => void;
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(undefined);

export const AnalysisProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progressStage, setProgressStage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Import the API functions dynamically to avoid circular dependencies
  const api = require('../services/api');

  const analyzeText = async (text: string, email?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.analyzePitchDeckText(text, email);
      setResult(data);
      
      if (email) {
        console.log(`Analysis results will be logged to Google Sheets for: ${email}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during analysis';
      setError(errorMessage);
      console.error('Analysis error:', errorMessage, err);
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeFile = async (file: File, email: string, country: string, privacyConsent: boolean, consentStorage?: boolean, consentImprovement?: boolean, industry?: string, stage?: string) => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setProgressStage('queued');
    try {
      const data = await api.uploadAndAnalyzeFile(
        file, email, country, privacyConsent, consentStorage, consentImprovement, industry, stage,
        undefined,
        (stageName: string) => setProgressStage(stageName)
      );
      setResult(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during file analysis';
      console.error('File analysis error:', errorMessage, err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setProgressStage(null);
    }
  };

  const clearResult = () => {
    setResult(null);
    setError(null);
  };

  return (
    <AnalysisContext.Provider
      value={{
        result,
        isLoading,
        progressStage,
        error,
        analyzeText,
        analyzeFile,
        clearResult,
      }}
    >
      {children}
    </AnalysisContext.Provider>
  );
};

export const useAnalysis = (): AnalysisContextType => {
  const context = useContext(AnalysisContext);
  if (context === undefined) {
    throw new Error('useAnalysis must be used within an AnalysisProvider');
  }
  return context;
};
